import React from 'react';
import { Typography } from 'antd';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';
import styles from './primitives.module.css';

const { Text } = Typography;

export interface MutationDiffRowV0 {
  readonly field: string;
  readonly before: string;
  readonly after: string;
}

export interface MutationDiffProps {
  readonly presentationState: PrimitivePresentationState;
  /** Preview rows only — never writes. */
  readonly previewRows?: readonly MutationDiffRowV0[];
  readonly previewLabel?: string;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  /** Always false — preview is not a completed mutation. */
  readonly provesMutationComplete?: false;
  readonly 'data-testid'?: string;
}

export function MutationDiff(props: MutationDiffProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  const provesMutationComplete = props.provesMutationComplete ?? false;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('MutationDiff must keep grantsAuthority/grantsApproval=false');
  }
  if (provesMutationComplete !== false) {
    throw new Error('MutationDiff is preview-only; provesMutationComplete must be false');
  }

  const rows = props.previewRows ?? [];

  return (
    <PrimitiveSurfaceFrame
      title="변경 미리보기"
      presentationState={props.presentationState}
      emptyMessage="미리보기 변경이 없습니다."
      data-testid={props['data-testid'] ?? 'mutation-diff'}
      aria-label="변경 미리보기"
    >
      <Text type="secondary">{props.previewLabel ?? '미리보기만 — 실행되지 않음'}</Text>
      {rows.length === 0 ? (
        <Text type="secondary">미리보기 행 없음</Text>
      ) : (
        <div className={styles.primitiveStack}>
          {rows.map((row) => (
            <div key={row.field} className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>{row.field}</span>
              <Text>
                {row.before} → {row.after}
              </Text>
            </div>
          ))}
        </div>
      )}
    </PrimitiveSurfaceFrame>
  );
}
